import { useEffect, useRef, useState, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Sidebar } from './Sidebar';
import { NotificationBell } from './NotificationBell';

const INACTIVITY_TIMEOUT = 8 * 60 * 1000; // 8 minutes

export function AppLayout({ children }) {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);

  const handleLogout = useCallback(() => {
    setShowWarning(false);
    logout();
    navigate('/login', { replace: true, state: { message: 'You were logged out due to inactivity' } });
  }, [logout, navigate]);

  const resetTimer = useCallback(() => {
    setShowWarning(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    // Show warning 1 minute before logout
    timerRef.current = setTimeout(() => {
      setShowWarning(true);
      // Auto-logout after 60 more seconds if no activity
      timerRef.current = setTimeout(handleLogout, 60 * 1000);
    }, INACTIVITY_TIMEOUT - 60 * 1000);
  }, [handleLogout]);

  useEffect(() => {
    if (!user) return;
    const events = ['mousedown', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    const onActivity = () => resetTimer();
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user, resetTimer]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFF]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#64748B] text-sm font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-[#F8FAFF]">
      <Sidebar />
      {showWarning && (
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div data-testid="inactivity-warning" className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm mx-4 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 3a9 9 0 110 18 9 9 0 010-18z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-[#0F172A] mb-2">Session Expiring</h3>
            <p className="text-sm text-[#64748B] mb-6">You'll be logged out in 60 seconds due to inactivity.</p>
            <button
              data-testid="stay-logged-in-btn"
              onClick={resetTimer}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-colors"
            >
              Stay Logged In
            </button>
          </div>
        </div>
      )}
      <main className="ml-[260px] min-h-screen transition-all duration-200">
        <div className="backdrop-blur-xl bg-white/80 border-b border-indigo-100/50 sticky top-0 z-30 px-8 py-3.5">
          <div className="flex items-center justify-between">
            <div />
            <div className="flex items-center gap-3">
              <NotificationBell />
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                {user.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <span className="text-sm text-[#0F172A] font-medium">{user.name}</span>
            </div>
          </div>
        </div>
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
